/**
 * Google Auth Callback route.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Google returns to `{origin}{returnTo}#session_id=…`. This route detects the
 * fragment, exchanges it for a server-side session cookie, then navigates to
 * the final destination. Uses a `useRef` guard so React 18 StrictMode double-
 * invocation doesn't cause a duplicate exchange.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useHubMember } from "@/lib/HubMemberContext";

export default function GoogleAuthCallback({ destination = "/portal" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useHubMember();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("No session_id in URL — please try signing in again.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        toast.success(`Signed in as ${res.data?.user?.name || "member"}`);
        // Clean the fragment so refreshing doesn't re-trigger
        window.history.replaceState({}, document.title, location.pathname);
        // Hydrate the member context BEFORE navigating, otherwise the destination
        // route's MemberGate sees ready=false and shows an endless "Loading…".
        await refresh();
        navigate(destination, { state: { user: res.data.user }, replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || "Sign-in failed — try again.");
      }
    })();
  }, [destination, navigate, location.pathname, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="google-auth-callback">
      <div className="text-center max-w-md">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Google Sign-In</div>
        <div className="font-display text-2xl font-black uppercase mt-2">
          {error ? "Sign-in failed" : "Finishing sign-in…"}
        </div>
        {error ? (
          <>
            <div className="text-red-300 text-sm mt-3">{error}</div>
            <button onClick={() => navigate("/parent/login", { replace: true })}
              className="mt-4 border border-brand-orange text-brand-orange px-4 py-2 text-xs font-mono uppercase tracking-widest">
              Back to login
            </button>
          </>
        ) : (
          <div className="text-white/50 text-xs mt-2">Please wait — we&apos;re opening your dashboard.</div>
        )}
      </div>
    </div>
  );
}
